import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, Users, CalendarDays, Coins, BarChart3, Clock, Banknote } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COLORS = ["#EA580C", "#84CC16", "#0F172A", "#94A3B8", "#3B82F6", "#EF4444", "#8B5CF6", "#F59E0B"];
const STATUS_COLORS = { CONFIRMED: "#22C55E", COMPLETED: "#22C55E", PENDING_CASH: "#F59E0B", PENDING_PAYMENT: "#F59E0B", CANCELLED: "#EF4444", EXPIRED: "#94A3B8" };

export default function ProviderAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/provider/analytics").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load analytics</div>;

  const s = data.summary;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="provider-analytics-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-2">
        <BarChart3 className="w-8 h-8 text-court-orange" />Analytics
      </h1>
      <p className="text-muted-foreground mb-8">Performance insights for your venues</p>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-10">
        {[
          { label: "Revenue", value: `₱${s.total_revenue?.toLocaleString()}`, icon: Coins, color: "text-lime-volt" },
          { label: "Total Bookings", value: s.total_bookings, icon: CalendarDays, color: "text-court-orange" },
          { label: "Confirmed", value: s.confirmed_bookings, icon: TrendingUp, color: "text-green-500" },
          { label: "Avg Value", value: `₱${s.avg_booking_value?.toFixed(0)}`, icon: Banknote, color: "text-blue-500" },
          { label: "Players", value: s.unique_players, icon: Users, color: "text-purple-500" },
          { label: "Venues", value: s.venues_count, icon: TrendingUp, color: "text-amber-500" },
          { label: "Courts", value: s.courts_count, icon: BarChart3, color: "text-slate-500" },
        ].map((st, i) => (
          <div key={i} className="bg-white border-2 border-slate-100 p-4" data-testid={`analytics-stat-${i}`}>
            <st.icon className={`w-4 h-4 ${st.color} mb-1.5`} />
            <p className="font-heading text-xl font-bold">{st.value}</p>
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">{st.label}</p>
          </div>
        ))}
      </div>

      {/* Revenue Trend */}
      <div className="bg-white border-2 border-slate-100 p-6 mb-6" data-testid="revenue-trend-chart">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-court-orange" />Revenue Trend (30 Days)
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => [`₱${v}`, "Revenue"]} labelFormatter={l => l} />
              <Line type="monotone" dataKey="revenue" stroke="#EA580C" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Peak Hours */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="peak-hours-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />Bookings by Hour
          </h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.peak_hours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" tick={{ fontSize: 9 }} tickFormatter={v => `${v}:00`} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [v, "Bookings"]} labelFormatter={l => `${l}:00`} />
                <Bar dataKey="bookings" fill="#EA580C" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Day Distribution */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="day-distribution-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4 flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />Bookings by Day
          </h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.day_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="bookings" fill="#84CC16" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Status Breakdown */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="status-breakdown-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Booking Status</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.bookings_by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={70} label={({ status, count }) => `${status.replace("_"," ")} (${count})`} labelLine={false}>
                  {data.bookings_by_status.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Sport Distribution */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="sport-distribution-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Sport Types</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.sport_distribution} dataKey="count" nameKey="sport" cx="50%" cy="50%" outerRadius={70} label={({ sport, count }) => `${sport} (${count})`} labelLine={false}>
                  {data.sport_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Split */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="payment-split-chart">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Payment Modes</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.payment_split} dataKey="count" nameKey="mode" cx="50%" cy="50%" outerRadius={70} label={({ mode, count }) => `${mode} (${count})`} labelLine={false}>
                  {data.payment_split.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Court Utilization */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="court-utilization">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Court Utilization</h3>
          {data.court_utilization?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No booking data yet</p>
          ) : (
            <div className="space-y-3">
              {data.court_utilization?.map((c, i) => {
                const max = Math.max(...data.court_utilization.map(x => x.bookings));
                const pct = max > 0 ? (c.bookings / max) * 100 : 0;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium truncate">{c.court}</span>
                      <span className="font-heading font-bold">{c.bookings}</span>
                    </div>
                    <div className="h-2 bg-slate-100 overflow-hidden">
                      <div className="h-full bg-court-orange transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Players */}
        <div className="bg-white border-2 border-slate-100 p-6" data-testid="top-players">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />Top Players
          </h3>
          {data.top_players?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No player data yet</p>
          ) : (
            <div className="space-y-2">
              {data.top_players?.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 text-sm">
                  <span className="font-heading text-lg font-bold text-court-orange w-6 text-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.bookings} bookings</p>
                  </div>
                  <span className="font-heading font-bold text-sm">₱{p.spent?.toFixed(0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
